<!-- 更多功能 -->
<script setup lang="ts">
import { defineAsyncComponent, markRaw, onBeforeUnmount, onMounted, ref, computed } from 'vue'
import DeleteConfirmModal from '~/components/DeleteConfirmModal.vue'
import type { MenuAction } from '~/types/generate'
import EditSongDetailsModal from './EditSongDetailsModal.vue'
import MoreActionMore from '~/assets/icons/moreActionMore.svg?component'
import PublishedIcon from '~/assets/icons/moreActionPublished.png'
import { useGenerateAction, useDownloadActions, useEditDetailsModal, usePublishAction, useDeleteAction } from '~/composables/useCommonFunction'
import { useShareModalStore } from '~/stores/shareModalStore'
import { useRoute } from '#imports'

const { handleGenerateAction } = useGenerateAction<any>()
const route = useRoute()

const formatDate = (value?: string | number | Date | null) => {
  if (!value) return ''
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return ''
  const y = date.getFullYear()
  const m = `${date.getMonth() + 1}`.padStart(2, '0')
  const d = `${date.getDate()}`.padStart(2, '0')
  return `${y}-${m}-${d}`
}

const props = defineProps<{
  // 整个歌曲对象，在组件内部依赖其字段渲染菜单
  song?: any
  // 可选的排除列表，用于在不同场景裁剪功能
  excludeIds?: string[]
}>()

const emit = defineEmits<{
  (e: 'update:isPublic', value: number): void // 通知父组件更新 isPublic 状态
  (e: 'deleted', uuid: string): void // 通知父组件歌曲已被删除
  (e: 'edit-success', payload: { title: string, lyrics: string }): void // 编辑成功后通知父组件
}>()

const menuActions = ref<MenuAction[]>([
  {
    id: 'remix',
    label: 'Remix',
    icon: markRaw(defineAsyncComponent(() => import('~/assets/icons/moreActionRemix.svg?component'))),
    submenu: [
      {
        id: 'extend',
        label: 'Extend',
        icon: markRaw(defineAsyncComponent(() => import('~/assets/icons/moreActionExtend.svg?component'))),
        badge: 'Pro',
        badgeVariant: 'pro'
      },
      {
        id: 'cover',
        label: 'Cover',
        icon: markRaw(defineAsyncComponent(() => import('~/assets/icons/moreActionCover.svg?component'))),
        badge: 'Pro',
        badgeVariant: 'pro'
      }
      // {
      //   id: 'adjust_speed',
      //   label: 'Adjust Speed',
      //   icon: markRaw(defineAsyncComponent(() => import('~/assets/icons/moreActionAdjustSpeed.svg?component')))
      // }
    ]
  },
  {
    id: 'download',
    label: 'Download',
    icon: markRaw(defineAsyncComponent(() => import('~/assets/icons/moreActionDownload.svg?component'))),
    submenu: [
      {
        id: 'download_mp3',
        label: 'Mp3',
        icon: markRaw(defineAsyncComponent(() => import('~/assets/icons/moreActionMusicNote.svg?component'))),
        badge: 'Pro',
        badgeVariant: 'pro'
      },
      {
        id: 'download_wav',
        label: 'Wav',
        icon: markRaw(defineAsyncComponent(() => import('~/assets/icons/moreActionMusicNote.svg?component'))),
        badge: 'Pro',
        badgeVariant: 'pro'
      }
      // {
      //   id: 'download_video',
      //   label: 'Video',
      //   icon: markRaw(defineAsyncComponent(() => import('~/assets/icons/moreActionMusicVideo.svg?component'))),
      //   badge: 'Pro',
      //   badgeVariant: 'pro'
      // }
    ]
  },
  {
    id: 'generate_similar',
    label: 'Generate similar',
    icon: markRaw(defineAsyncComponent(() => import('~/assets/icons/moreActionGenerateSimilar.svg?component')))
  },
  {
    id: 'edit_details',
    label: 'Edit song\'s details',
    icon: markRaw(defineAsyncComponent(() => import('~/assets/icons/moreActionEditDetails.svg?component')))
  },
  // {
  //   id: 'add_to_playlist',
  //   label: 'Add to playlist',
  //   icon: markRaw(defineAsyncComponent(() => import('~/assets/icons/moreActionAddPlaylist.svg?component'))),
  //   type: 'playlist'
  // },
  // {
  //   id: 'add_to_queue',
  //   label: 'Add to queue',
  //   icon: markRaw(defineAsyncComponent(() => import('~/assets/icons/moreActionAddQueue.svg?component')))
  // },
  {
    id: 'publish',
    label: 'Publish',
    icon: markRaw(defineAsyncComponent(() => import('~/assets/icons/moreActionPublish.svg?component')))
  },
  {
    id: 'share',
    label: 'Share',
    icon: markRaw(defineAsyncComponent(() => import('~/assets/icons/moreActionShare.svg?component')))
  },
  // {
  //   id: 'report',
  //   label: 'Report',
  //   icon: markRaw(defineAsyncComponent(() => import('~/assets/icons/moreActionReport.svg?component')))
  // },
  {
    id: 'move_to_trash',
    label: 'Move to trash',
    icon: markRaw(defineAsyncComponent(() => import('~/assets/icons/moreActionDelete.svg?component')))
  }
])

// 媒体环境：区分桌面端和移动端（用于子菜单触发方式）
const isMobile = ref(false)

function updateIsMobile() {
  if (typeof window === 'undefined') return
  isMobile.value = window.innerWidth < 768
}

// 根据 excludeIds 过滤主菜单和其子菜单
const filteredMenuActions = computed(() => {
  if (!props.excludeIds || props.excludeIds.length === 0) {
    return menuActions.value
  }

  return menuActions.value
    .filter(action => !props.excludeIds!.includes(action.id))
    .map((action) => {
      if (action.submenu) {
        const filteredSubmenu = action.submenu.filter(
          subAction => !props.excludeIds!.includes(subAction.id)
        )
        // 如果所有子菜单都被过滤掉了，则过滤掉整个主菜单项
        if (filteredSubmenu.length === 0) {
          return null
        }
        return {
          ...action,
          submenu: filteredSubmenu
        }
      }
      return action
    })
    .filter((action): action is MenuAction => action !== null)
})

// 获取 publish 按钮的图标和文字，根据当前歌曲是否已发布，动态切换按钮文案与图标
const publishActionConfig = computed(() => {
  const isPublished = props.song?.is_public === 1
  return {
    label: isPublished ? 'UnPublish' : 'Publish',
    icon: isPublished ? PublishedIcon : undefined, // Published 是图片，Publish 是组件
    isImage: isPublished
  }
})

const mainPopoverOpen = ref(false)

// 处理点击 'extend' | 'cover' | 'generate_similar' 按钮功能
async function navigateToGenerateWithAction(action: 'extend' | 'cover' | 'generate_similar') {
  if (!props.song) {
    return
  }
  const isHomePage = route.path === '/' || route.name === 'index'
  if (isHomePage && action === 'generate_similar') {
    await handleGenerateAction(
      action,
      { song: props.song, lockSongAction: true },
      { wrapSong: false }
    )
  } else {
    await handleGenerateAction(action, props.song)
  }
  mainPopoverOpen.value = false
}

// #region 下载相关（使用通用 composable）
const {
  handleDownloadMp3,
  handleDownloadWav,
  wavDownloadLoading,
  cancelWavPolling
} = useDownloadActions<any>({
  getMp3Url: song => song?.fileInfo?.mp3Url || song?.fileInfo?.cosUrl,
  getClipId: (song) => {
    const rawFullPrompt = song?.fullPrompt as any
    if (rawFullPrompt) {
      if (typeof rawFullPrompt === 'string') {
        try {
          const parsed = JSON.parse(rawFullPrompt)
          if (parsed?.clip_id) return parsed.clip_id
        } catch {
          // ignore parse errors
        }
      } else if (typeof rawFullPrompt === 'object' && rawFullPrompt !== null && 'clip_id' in rawFullPrompt) {
        return (rawFullPrompt as { clip_id?: string }).clip_id
      }
    }
    return (song as any)?.clip_id || song?.uuid
  },
  getTitle: song => song?.title || song?.fullPrompt?.gpt_description_prompt || 'song'
})

async function handleDownloadMp3Action() {
  await handleDownloadMp3(props.song)
  mainPopoverOpen.value = false
}

async function handleDownloadWavAction() {
  await handleDownloadWav(props.song)
  mainPopoverOpen.value = false
}
// #endregion

// #region 编辑相关（使用通用 composable）
const {
  editModalOpen,
  currentSong,
  openEditDetails,
  handleEditModalToggle
} = useEditDetailsModal<any>()

function handleEditDetailsAction() {
  openEditDetails(props.song)
  mainPopoverOpen.value = false
}

const handleEditModalSaved = (payload: { title: string, lyrics: string }) => {
  emit('edit-success', payload)
}
// #endregion

// #region 公布相关
const {
  publishLoading,
  handlePublishAction
} = usePublishAction<any>({
  onUpdated: isPublic => emit('update:isPublic', isPublic)
})

async function handlePublishActionWrapper() {
  const success = await handlePublishAction(props.song)
  if (success) {
    mainPopoverOpen.value = false
  }
}
// #endregion

// #region 分享相关
const shareModalStore = useShareModalStore()
function handleShareAction() {
  mainPopoverOpen.value = false
  const title = props.song?.metadata?.type === 'upload' && props.song?.title === 'my_2024-09-04_01_37_04' && props.song?.fullPrompt?.title || props.song?.title || props.song?.metadata?.prompt || `Untitled - ${formatDate(props.song?.createdAt)}`

  shareModalStore.openShare({
    title,
    uuid: props.song?.uuid
  })
}
// #endregion

// #region 删除相关
const {
  deleteLoading,
  deleteConfirmOpen,
  handleDeleteAction,
  handleConfirmDelete,
  handleCancelDelete
} = useDeleteAction<any>({
  onDeleted: uuid => emit('deleted', uuid)
})

async function handleMoveToTrashAction() {
  await handleDeleteAction(props.song)
}

async function handleConfirmDeleteModal() {
  const success = await handleConfirmDelete()
  if (success) {
    mainPopoverOpen.value = false
  }
}
// #endregion

// 根据菜单 id 分发到各自处理器
async function handleActionClick(action: MenuAction) {
  if (action.id === 'extend') {
    await navigateToGenerateWithAction('extend')
    return
  }
  if (action.id === 'cover') {
    await navigateToGenerateWithAction('cover')
    return
  }
  if (action.id === 'download_mp3') {
    await handleDownloadMp3Action()
    return
  }
  if (action.id === 'download_wav') {
    await handleDownloadWavAction()
    return
  }
  if (action.id === 'generate_similar') {
    await navigateToGenerateWithAction('generate_similar')
    return
  }
  if (action.id === 'edit_details') {
    handleEditDetailsAction()
    return
  }
  if (action.id === 'publish') {
    await handlePublishActionWrapper()
    return
  }
  if (action.id === 'share') {
    handleShareAction()
    return
  }
  if (action.id === 'move_to_trash') {
    await handleMoveToTrashAction()
    return
  }
}

// 处理点击子操作
function handleSubActionClick(subAction: MenuAction) {
  void handleActionClick(subAction)
}

onMounted(() => {
  updateIsMobile()
  if (typeof window !== 'undefined') {
    window.addEventListener('resize', updateIsMobile)
  }
})

onBeforeUnmount(() => {
  cancelWavPolling()
  if (typeof window !== 'undefined') {
    window.removeEventListener('resize', updateIsMobile)
  }
})
</script>

<template>
  <ClientOnly>
    <UPopover
      v-model:open="mainPopoverOpen"
      mode="click"
      :content="{ side: 'right', align: 'start', sideOffset: 4 }"
      :ui="{
        content: 'bg-[#383847] border border-[#5E5E77] rounded-[8px] ring-0 overflow-hidden'
      }"
    >
      <template #default>
        <slot />
      </template>
      <template #content>
        <div
          v-for="action in filteredMenuActions"
          :key="action.id"
        >
          <!-- 带子菜单的按钮 -->
          <UPopover
            v-if="action.submenu"
            :mode="isMobile ? 'click' : 'hover'"
            :content="{ side: 'right', align: 'start', sideOffset: isMobile ? -8 : 4 }"
            :ui="{
              content: 'bg-[#383847] border border-[#5E5E77] rounded-[8px] ring-0 overflow-hidden'
            }"
          >
            <template #default>
              <div class="flex items-center justify-between w-full text-[16px] md:text-[14px] text-[#E7E7EC] leading-[24px] md:leading-[20px] hover:bg-[#262635] px-[12px] py-[8px] md:py-[6px] transition-colors cursor-pointer">
                <span class="flex items-center gap-[8px]">
                  <UIcon
                    :name="action.icon"
                    class="size-[18px] md:size-[16px]"
                  />
                  {{ action.label }}
                </span>
                <MoreActionMore class="size-[18px] md:size-[16px]" />
              </div>
            </template>
            <template #content>
              <div
                v-for="subAction in action.submenu"
                :key="subAction.id"
                :class="[
                  'w-full flex items-center gap-[8px] text-[16px] md:text-[14px] text-[#E7E7EC] leading-[24px] md:leading-[20px] hover:bg-[#262635] px-[12px] py-[8px] md:py-[6px] transition-colors cursor-pointer',
                  subAction.badgeVariant === 'pro' ? 'text-[#FFE8BD]' : 'text-[#E7E7EC]',
                  subAction.id === 'download_wav' && wavDownloadLoading ? 'opacity-60 cursor-wait pointer-events-none' : ''
                ]"
                @click="handleSubActionClick(subAction)"
              >
                <span class="flex items-center gap-[8px]">
                  <UIcon
                    v-if="!(subAction.id === 'download_wav' && wavDownloadLoading)"
                    :name="subAction.icon"
                    class="size-[18px] md:size-[16px]"
                  />
                  <UIcon
                    v-else
                    name="svg-spinners:90-ring-with-bg"
                    class="size-[18px] md:size-[16px]"
                  />
                  {{ subAction.label }}
                </span>
                <span
                  v-if="subAction.badge"
                  :class="[
                    'text-[10px] leading-[12px] px-[6px] py-[2px] rounded-[4px]',
                    subAction.badgeVariant === 'pro' ? 'bg-[#FFE8BD40] text-[#FFE8BD]' : 'bg-[#383847] text-[#E7E7EC]'
                  ]"
                >
                  {{ subAction.badge }}
                </span>
              </div>
            </template>
          </UPopover>
          <!-- 普通功能按钮 -->
          <div
            v-else
            class="flex items-center justify-between w-full text-[16px] md:text-[14px] text-[#E7E7EC] leading-[24px] md:leading-[20px] hover:bg-[#262635] px-[12px] py-[8px] md:py-[6px] transition-colors cursor-pointer"
            @click="handleActionClick(action)"
          >
            <span class="flex items-center gap-[8px]">
              <!-- Publish 按钮根据 isPublic 状态显示不同图标 -->
              <template v-if="action.id === 'publish'">
                <img
                  v-if="publishActionConfig.isImage"
                  :src="publishActionConfig.icon"
                  alt="Published"
                  class="size-[18px] md:size-[16px]"
                >
                <UIcon
                  v-else
                  :name="action.icon"
                  class="size-[18px] md:size-[16px]"
                />
              </template>
              <!-- 其他按钮使用默认图标 -->
              <UIcon
                v-else
                :name="action.icon"
                class="size-[18px] md:size-[16px]"
              />
              {{ action.id === 'publish' ? publishActionConfig.label : action.label }}
            </span>
          </div>
        </div>
      </template>
    </UPopover>

    <template #fallback>
      <slot />
    </template>
  </ClientOnly>

  <!-- 删除确认弹窗 -->
  <DeleteConfirmModal
    :open="deleteConfirmOpen"
    :loading="deleteLoading"
    @cancel="handleCancelDelete"
    @confirm="handleConfirmDeleteModal"
  />

  <!-- 编辑详情弹窗 -->
  <EditSongDetailsModal
    :open="editModalOpen"
    :song="currentSong"
    @update:open="handleEditModalToggle"
    @saved="handleEditModalSaved"
  />
</template>
